/** @jsxImportSource solid-js */
import { createSignal } from "solid-js";
import { createCheckboxGroup } from "#palette/components/form/checkbox.solid";
import { createDeclineableOther } from "#palette/components/form/declineable.solid";
import { createRadioGroup } from "#palette/components/form/radio.solid";
import { SubmitButton } from "#palette/components/form/submit.solid";
import { createTextInput } from "#palette/components/form/text.solid";
import { Spinner } from "#palette/components/spinner.solid";
import { globalState } from "#root/frontend/state.jsx";
import { Icons } from "#root/lib/palette/components/icons.solid.js";
import { TooltipBase } from "#root/lib/palette/components/tooltip.solid.js";

const campusAffiliationOptions = [
  "Undergraduate Student",
  "Graduate/Professional Student",
  "Faculty Member",
  "Other",
];
const campusClassLevel = [
  "1st year",
  "2nd year",
  "3rd year",
  "4th year",
  "5th year or higher",
  "Other",
];
const ethnicityOptions = [
  "Native American or Alaska Native",
  "Asian",
  "Black or African American",
  "Hispanic/Latino",
  "Native Hawaiian or Other Pacific Islander",
  "White",
  "Other",
];
const genderIdentityOptions = [
  "Woman",
  "Man",
  "Trans Woman",
  "Trans Man",
  "Nonbinary/genderqueer/Gender non-conforming",
  "Questioning or Not Listed",
];
const sexualOrientationOptions = [
  "Heterosexual or straight",
  "Gay or Lesbian",
  "Bisexual",
  "Asexual",
  "Queer",
  "Questioning",
  "Intersex", // TODO: this one seems incongruous
  "Pansexual",
  "Other",
];
const disabilityOptions = [
  "I do not have a disability or chronic condition",
  "Learning disability",
  "ADHD",
  "Autism Spectrum Disorder",
  "Mobility-related disability (e.g., spinal cord injury, muscular dystrophy, etc.)",
  "Sensory disability (e.g., hard of hearing, low vision, etc.)",
  "Chronic medical condition (e.g., cystic fibrosis, diabetes, chronic pain, etc.)",
  "Other disability or chronic condition",
];
const extracurricularOptions = [
  "No extracurricular activities",
  "Student Government",
  "Sexual Assault Advocacy",
  "Multicultural activities and organizations",
  "Intercollegiate Athletics",
  "Greek Life",
  "Religious organizations",
  "Professional/departmental club",
  "College Production and/or performances",
  "College media",
  "Other Activity",
];
const howLearnedOptions = [
  "From a friend",
  "Campus Event",
  "Social Media",
  "Desi Dance Network Partnership",
  "News/Article/Video",
  "Web Search",
  "Other",
];

/**
 * Show a page for users to enter their demographics information.
 */
export function Step_5_Demographics(props: {
  goNext: () => void;
  setError: (m: string) => void;
  resetError: () => void;
}) {
  const [loading, setLoading] = createSignal(false);

  // Actual demographics ///////////////////////////////////////////////////////
  const ageInner = createTextInput({ label: "What's your age?" });
  const ageInput = createDeclineableOther(ageInner, "I decline to answer");

  const campusAffiliationInner = createRadioGroup(
    "What's your affiliation with your campus?",
    campusAffiliationOptions.map((e) => {
      return { label: e };
    }),
    -1,
  );
  const campusAffiliationInput = createDeclineableOther(
    campusAffiliationInner,
    "I decline to answer",
  );

  const campusClassLevelInner = createRadioGroup(
    "What is your class year in school? (Answer on the basis of the number of credits you have earned.)",
    campusClassLevel.map((e) => {
      return { label: e };
    }),
    -1,
  );
  const campusClassLevelInput = createDeclineableOther(
    campusClassLevelInner,
    "I decline to answer",
  );

  const ethnicityInner = createCheckboxGroup(
    ethnicityOptions.map((e) => {
      return { label: e, selected: false };
    }),
    "What races/ethnicities that best describe you? (Mark all that apply.)",
  );
  const ethnicityInput = createDeclineableOther(ethnicityInner, [
    "I decline to answer",
  ]);

  const genderIdentityInner = createRadioGroup(
    "Which best describes your gender identity?",
    genderIdentityOptions.map((e) => {
      return { label: e };
    }),
    -1,
  );
  const genderIdentityInput = createDeclineableOther(
    genderIdentityInner,
    "I decline to answer",
  );

  const sexualOrientationInner = createRadioGroup(
    "Do you consider yourself to be:",
    sexualOrientationOptions.map((e) => {
      return { label: e };
    }),
    -1,
  );
  const sexualOrientationInput = createDeclineableOther(
    sexualOrientationInner,
    "I decline to answer",
  );

  const disabilityInner = createCheckboxGroup(
    disabilityOptions.map((e) => {
      return { label: e, selected: false };
    }),
    "If you identify as a person with any of the following, please mark all that apply.",
  );
  const disabilityInput = createDeclineableOther(disabilityInner, [
    "I decline to answer",
  ]);

  const extracurricularInner = createCheckboxGroup(
    extracurricularOptions.map((e) => {
      return { label: e, selected: false };
    }),
    "Please select all extracurricular activities you are involved in:",
  );
  const extracurricularInput = createDeclineableOther(extracurricularInner, [
    "I decline to answer",
  ]);

  const howLearnedInner = createRadioGroup(
    "How did you learn about Callisto?",
    howLearnedOptions.map((e) => {
      return { label: e };
    }),
    -1,
  );
  const howLearnedInput = createDeclineableOther(
    howLearnedInner,
    "I decline to answer",
  );
  let formElement!: HTMLFormElement;

  function submitDemographics() {
    if (
      ageInput.value() === "" ||
      campusAffiliationInput.value() === undefined ||
      campusClassLevelInput.value() === undefined ||
      ethnicityInput.value().length === 0 ||
      genderIdentityInput.value() === undefined ||
      sexualOrientationInput.value() === undefined ||
      disabilityInput.value().length === 0 ||
      extracurricularInput.value().length === 0 ||
      howLearnedInput.value() === undefined
    ) {
      formElement.setAttribute("data-validate", "true");
      return;
    }

    setLoading(true);
    setTimeout(async () => {
      await globalState.client
        .saveDemographicData(
          // TODO: turn this into one that only accepts numbers
          parseInt(ageInput.value(), 10),
          campusAffiliationInput.value(),
          campusClassLevelInput.value(),
          ethnicityInput.value(),
          genderIdentityInput.value(),
          sexualOrientationInput.value(),
          disabilityInput.value(),

          // Create an "Other" input for these that also works
          howLearnedInput.value(),
          extracurricularInput.value(),
        )
        .then(() => {
          // TODO: find a way to not need to hack this?
          globalState.client.demographicsComplete = true;
          props.goNext();
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 100);
  }

  // TODO: success callout?
  return (
    <>
      <div class="mb-3 mt-6">
        <h1 class="text-xl font-bold text-brand-900 font-serif inline">
          Enter your demographics
        </h1>
        <span class="relative group/tooltip ml-2">
          <Icons.CircleInfo />
          <TooltipBase>
            <p class="py-2 font-bold">Why do we need this?</p>
            <p class="py-2">
              In order for Callisto to maintain a free service for survivors, we
              seek out grant funding from foundations, individuals, and
              governments. In doing so, it is essential that we are able to
              discuss the impact that our work is having, particularly for
              people in historically underserved and marginalized communities.
              As such, we have begun collecting some basic demographic
              information. This information will NOT be connected with any of
              your record forms or entries. The demographic data collected will
              only be shared in aggregate form and therefore, it will not be
              identifiable.
            </p>
            <p class="py-2">
              We appreciate your willingness to provide this information as it
              will not only help us continue this as a free service, but also to
              help us improve our platform. While your participation in the
              survey is required, each question has an option to “decline to
              answer” if you do not wish to disclose certain or any information.
            </p>
            <p class="py-2">
              Thank you for helping Callisto to continue to support survivors in
              their journey towards healing and justice.
            </p>
          </TooltipBase>
        </span>
      </div>

      <form class="group" data-validate="false" ref={formElement}>
        <div>
          {ageInput.component}
          {campusAffiliationInput.component}
          {campusClassLevelInput.component}
          {ethnicityInput.component}
          {genderIdentityInput.component}
          {sexualOrientationInput.component}
          {disabilityInput.component}
          {extracurricularInput.component}
          {howLearnedInput.component}
        </div>
        <SubmitButton
          onClick={submitDemographics}
          disabled={false}
          title="Finish Creating Your Account"
        />
      </form>
      {loading() && <Spinner />}
    </>
  );
}
